import Mock from 'mockjs'
import { getStoreEmployees, getStoreResigned } from './employees'
import { getStoreBehaviors, getStoreCategories } from './behaviors'
import { getStoreCases } from './cases'
import { evaluations, trendData, getThresholds } from './evaluations'
import { getStoreInstructions, getActiveInstruction } from './evaluationInstructions'
import { getStoreScoringRules } from './scoringRules'
import { getStoreReportContents } from './reportContentConfigs'
import { getStoreDepartureFeedbacks } from './departureFeedbacks'
import { getStoreUsers } from './users'
import { defaultTasks } from './tasks'
import { defaultAdmins } from './admins'
import { getStoreSurveyQuestions } from './surveyQuestions'

function getStore() {
  const s = require('../store').default
  return s
}

const success = (data) => ({ code: 200, data, message: 'success' })
const fail = (code, message) => ({ code, data: null, message })

// ==================== 认证 ====================

Mock.mock('/api/v3/auth/login', 'post', (options) => {
  const body = JSON.parse(options.body)
  const users = getStoreUsers()
  const user = users.find(u => u.email === body.email)
  if (user) {
    if (user.status === 'disabled') {
      return fail(403, '账号已被禁用，请联系管理员')
    }
    if (user.password !== body.password) {
      return fail(401, '邮箱或密码错误')
    }
    // 主管登录时，从其员工记录中获取 task_id
    const empList = getStoreEmployees()
    const emp = empList.find(e => e.email === user.email)
    const taskIds = emp && emp.task_id ? [emp.task_id] : []
    // 主管登录时初始化 tasksList 并设置 currentTask
    const s = getStore()
    if (!s.state.tasks.tasksList || !s.state.tasks.tasksList.length) {
      s.commit('SET_TASKS_LIST', defaultTasks)
    }
    if (taskIds.length > 0) {
      const target = defaultTasks.find(t => String(t.task_id) === String(taskIds[0]))
      if (target) {
        s.commit('SET_CURRENT_TASK', target)
      }
    }
    return success({
      token: 'mock-jwt-token-' + user.email,
      email: user.email,
      role: user.role,
      real_name: user.email.split('@')[0],
      is_first_login: user.is_first_login,
      task_ids: taskIds
    })
  }
  const s = getStore()
  const admins = s.state.admins && s.state.admins.admins && s.state.admins.admins.length
    ? s.state.admins.admins
    : defaultAdmins
  const admin = admins.find(a => a.email === body.email)
  if (admin) {
    if (admin.status !== 1) {
      return fail(403, '账号已被禁用，请联系管理员')
    }
    if (admin.password !== body.password) {
      return fail(401, '邮箱或密码错误')
    }
    // 首次登录时把 defaultAdmins 写入 store，确保后续管理页面有数据
    if (!s.state.admins.admins || !s.state.admins.admins.length) {
      s.commit('SET_ADMINS', defaultAdmins)
    }
    return success({
      token: 'mock-jwt-token-' + admin.email,
      email: admin.email,
      role: admin.role,
      real_name: admin.real_name,
      is_first_login: false,
      task_ids: admin.task_ids || []
    })
  }
  return fail(401, '邮箱或密码错误')
})

Mock.mock('/api/v3/auth/set-password', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  s.commit('UPDATE_MANAGER_PASSWORD', { email: body.email, password: body.password })
  return success({ success: true })
})

// ==================== 任务 ====================

Mock.mock('/api/v3/admin/tasks', 'get', () => {
  const s = getStore()
  const list = s.state.tasks && s.state.tasks.tasksList && s.state.tasks.tasksList.length
    ? s.state.tasks.tasksList
    : defaultTasks
  return success(list)
})

Mock.mock('/api/v3/admin/tasks', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const taskId = Date.now()
  const task = { task_id: taskId, ...body, created_at: new Date().toISOString() }
  s.commit('ADD_TASK', task)
  return success({ task_id: taskId })
})

Mock.mock(/\/api\/v3\/admin\/tasks\/\d+/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  s.commit('UPDATE_TASK', body)
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/tasks\/\d+/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/tasks\/(\d+)/)[1])
  const s = getStore()
  s.commit('DELETE_TASK', id)
  return success({ success: true })
})

// ==================== 员工 ====================

Mock.mock('/api/v3/employees/subordinates', 'get', () => {
  const list = getStoreEmployees()
  return success(list)
})

Mock.mock(/\/api\/v3\/employees\/search.*/, 'get', () => {
  const list = getStoreEmployees()
  return success(list)
})

Mock.mock('/api/v3/admin/employees', 'get', () => {
  const list = getStoreEmployees()
  return success(list)
})

Mock.mock('/api/v3/admin/employees', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const empId = Date.now()
  const emp = { employee_id: empId, ...body }
  s.commit('ADD_EMPLOYEE', emp)
  return success({ employee_id: empId })
})

Mock.mock(/\/api\/v3\/admin\/employees\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  s.commit('UPDATE_EMPLOYEE', body)
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/employees\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/employees\/(\d+)/)[1])
  const s = getStore()
  s.commit('DELETE_EMPLOYEE', id)
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/employees\/\d+\/resign$/, 'post', (options) => {
  const id = parseInt(options.url.match(/\/employees\/(\d+)/)[1])
  const body = JSON.parse(options.body)
  const s = getStore()
  s.commit('MARK_RESIGNED', { id, ...body, manager_email: s.state.user && s.state.user.email })
  return success({ success: true })
})

Mock.mock('/api/v3/admin/resigned', 'get', () => {
  const list = getStoreResigned()
  return success(list)
})

Mock.mock(/\/api\/v3\/admin\/employees\/\d+\/restore$/, 'post', (options) => {
  const id = parseInt(options.url.match(/\/employees\/(\d+)/)[1])
  const s = getStore()
  s.commit('RESTORE_EMPLOYEE', id)
  return success({ success: true })
})

// ==================== 行为库 ====================

Mock.mock('/api/v3/behaviors/list', 'get', () => {
  const list = getStoreBehaviors()
  return success(list)
})

Mock.mock('/api/v3/behaviors/categories', 'get', () => {
  const list = getStoreCategories()
  return success(list)
})

Mock.mock(/\/api\/v3\/behaviors\/\d+\/objective-data$/, 'get', (options) => {
  const id = parseInt(options.url.match(/\/behaviors\/(\d+)/)[1])
  const summaries = {
    103: '近3个月请假12次，较上季度增加300%',
    107: '当前薪资低于同职级中位数15%，最近一次调薪为2023年6月',
    112: '招聘平台活跃度监测显示，近30天更新简历2次，被猎头查看15次'
  }
  return success({
    behavior_id: id,
    has_data: id in summaries,
    data_summary: summaries[id] || '暂无系统数据',
    last_updated: '2026-05-01'
  })
})

Mock.mock('/api/v3/admin/behaviors', 'get', () => {
  const list = getStoreBehaviors()
  return success(list)
})

Mock.mock('/api/v3/admin/behaviors', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const behaviorId = Date.now()
  const item = { behavior_id: behaviorId, ...body, status: 2 }
  const list = s.state.behaviors && s.state.behaviors.behaviors ? [...s.state.behaviors.behaviors] : []
  list.push(item)
  s.commit('SET_BEHAVIORS', list)
  return success({ behavior_id: behaviorId })
})

Mock.mock(/\/api\/v3\/admin\/behaviors\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const list = s.state.behaviors && s.state.behaviors.behaviors ? [...s.state.behaviors.behaviors] : []
  const idx = list.findIndex(b => b.behavior_id === body.behavior_id)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...body }
    s.commit('SET_BEHAVIORS', list)
  }
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/behaviors\/\d+\/toggle$/, 'post', (options) => {
  const id = parseInt(options.url.match(/\/behaviors\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.behaviors && s.state.behaviors.behaviors ? [...s.state.behaviors.behaviors] : []
  const idx = list.findIndex(b => b.behavior_id === id)
  if (idx >= 0) {
    const newStatus = list[idx].status === 1 ? 3 : 1
    list[idx] = { ...list[idx], status: newStatus }
    s.commit('SET_BEHAVIORS', list)
    return success({ success: true, status: newStatus })
  }
  return fail(404, '行为项不存在')
})

Mock.mock('/api/v3/admin/behaviors/categories', 'post', (options) => {
  const body = JSON.parse(options.body)
  const catId = Date.now()
  return success({ category_id: catId })
})

Mock.mock(/\/api\/v3\/admin\/behaviors\/categories\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const list = s.state.behaviors && s.state.behaviors.categories ? [...s.state.behaviors.categories] : getStoreCategories()
  const idx = list.findIndex(c => c.category_id === body.category_id)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...body }
    s.commit('SET_CATEGORIES', list)
  }
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/behaviors\/categories\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/categories\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.behaviors && s.state.behaviors.categories ? [...s.state.behaviors.categories] : getStoreCategories()
  const idx = list.findIndex(c => c.category_id === id)
  if (idx >= 0) {
    list[idx] = { ...list[idx], status: 2 }
    s.commit('SET_CATEGORIES', list)
  }
  return success({ success: true })
})

// ==================== 评估 ====================

Mock.mock('/api/v3/evaluations', 'post', () => {
  return success({ evaluation_id: 60001, status: 1 })
})

Mock.mock(/\/api\/v3\/evaluations\/\d+\/answer$/, 'post', (options) => {
  const body = JSON.parse(options.body)
  const bList = getStoreBehaviors()
  const behavior = bList.find(b => b.behavior_id === body.behavior_id)
  const t = getThresholds()
  const score = body.answer === 1 ? (behavior && behavior.score) || 0 : 0
  return success({
    current_score: score,
    risk_level: score > t.mediumMax ? 3 : (score > t.lowMax ? 2 : 1),
    progress: '1/17'
  })
})

Mock.mock(/\/api\/v3\/evaluations\/\d+\/submit$/, 'post', () => {
  const cList = getStoreCases()
  const contents = getStoreReportContents()
  const suggestions = contents.filter(c => c.type === 1 && c.status === 1).slice(0, 3)
  const cases = contents.filter(c => c.type === 2 && c.status === 1).slice(0, 3)
  const courses = contents.filter(c => c.type === 3 && c.status === 1).slice(0, 3)
  return success({
    evaluation_id: 60001,
    total_score: 25,
    risk_level: 3,
    risk_analysis: '该员工的离职风险主要源于"发展受限"（权重40%）和"外部机会"（权重35%），而非薪酬因素（5%）。',
    matched_suggestions: suggestions.map(s => ({ title: s.title, content: s.content })),
    matched_cases: cases.map(c => ({ title: c.title, content: c.content })),
    recommended_courses: courses.map(c => ({ title: c.title, content: c.content }))
  })
})

Mock.mock('/api/v3/evaluations/history', 'get', () => {
  return success(evaluations)
})

Mock.mock(/\/api\/v3\/evaluations\/trend\/\d+$/, 'get', (options) => {
  const id = parseInt(options.url.match(/\/trend\/(\d+)/)[1])
  const empList = getStoreEmployees()
  const emp = empList.find(e => e.employee_id === id)
  return success({
    employee_name: (emp && emp.real_name) || '',
    trend_data: trendData[id] || []
  })
})

Mock.mock('/api/v3/admin/evaluations', 'get', () => {
  return success(evaluations)
})

Mock.mock(/\/api\/v3\/admin\/evaluations\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/evaluations\/(\d+)/)[1])
  const s = getStore()
  s.commit('SET_EVALUATIONS', (s.state.evaluations || []).filter(e => e.evaluation_id !== id))
  return success({ success: true })
})

// ==================== 数据看板 ====================

Mock.mock('/api/v3/dashboard/heatmap', 'get', () => {
  const list = getStoreEmployees()
  const t = getThresholds()
  return success({
    high_risk: list.filter(e => e.latest_score > t.mediumMax).sort((a, b) => b.latest_score - a.latest_score).map(e => ({ employee_id: e.employee_id, real_name: e.real_name, latest_score: e.latest_score, trend: 'up' })),
    medium_risk: list.filter(e => e.latest_score > t.lowMax && e.latest_score <= t.mediumMax).sort((a, b) => b.latest_score - a.latest_score).map(e => ({ employee_id: e.employee_id, real_name: e.real_name, latest_score: e.latest_score, trend: 'flat' })),
    low_risk: list.filter(e => e.latest_score <= t.lowMax).sort((a, b) => a.latest_score - b.latest_score).map(e => ({ employee_id: e.employee_id, real_name: e.real_name, latest_score: e.latest_score, trend: 'down' })),
    unassessed_count: 0
  })
})

Mock.mock('/api/v3/dashboard/ab-compare', 'get', () => {
  const list = getStoreEmployees()
  const bList = getStoreBehaviors()
  return success({
    employee_a: { employee_id: list[0] && list[0].employee_id, real_name: list[0] && list[0].real_name, total_score: 25, risk_level: 3 },
    employee_b: { employee_id: list[1] && list[1].employee_id, real_name: list[1] && list[1].real_name, total_score: 8, risk_level: 2 },
    score_diff: 17,
    comparison: bList.map(b => ({
      behavior_id: b.behavior_id,
      description: b.description,
      employee_a_status: Math.random() > 0.6 ? '是' : '否',
      employee_a_score: Math.random() > 0.6 ? b.score : 0,
      employee_b_status: Math.random() > 0.7 ? '是' : '否',
      employee_b_score: Math.random() > 0.7 ? b.score : 0
    }))
  })
})

Mock.mock('/api/v3/dashboard/stats', 'get', () => {
  const list = getStoreEmployees()
  return success({
    total_subordinates: list.length,
    high_risk_count: list.filter(e => e.latest_risk_level === 3).length,
    medium_risk_count: list.filter(e => e.latest_risk_level === 2).length,
    low_risk_count: list.filter(e => e.latest_risk_level === 1).length,
    unassessed_count: list.filter(e => e.latest_score === 0).length
  })
})

// ==================== 调研 ====================

Mock.mock('/api/v3/surveys', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const token = 'mock-survey-' + Date.now()
  const expireAt = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
  const empName = body.employee_name || '未知员工'
  const survey = {
    survey_id: Date.now(),
    token: token,
    employee_name: empName,
    link: '/survey?token=' + token,
    created_at: new Date().toISOString(),
    status: 'pending',
    task_id: body.task_id || 1
  }
  s.commit('ADD_SURVEY', survey)
  return success({
    survey_token: token,
    survey_url: survey.link,
    expire_at: expireAt.toISOString().replace('T', ' ').slice(0, 19)
  })
})

Mock.mock('/api/v3/surveys/submit', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const resp = {
    response_id: Date.now(),
    survey_token: body.survey_token,
    employee_name: '匿名员工',
    primary_reasons: body.primary_reasons || [],
    other_reason: body.other_reason || '',
    behaviors_confirmed: body.behavior_confirmations || [],
    subjective_feedback: body.subjective_feedback || '',
    submitted_at: new Date().toISOString()
  }
  s.commit('ADD_RESPONSE', resp)
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/surveys\/[^/]+$/, 'get', (options) => {
  const token = options.url.match(/\/surveys\/([^/]+)/)[1]
  const s = getStore()
  const surveys = s.state.surveys && s.state.surveys.surveys ? s.state.surveys.surveys : []
  let survey = surveys.find(s => s.token === token)
  // 未找到时创建默认问卷（用于测试）
  if (!survey) {
    survey = {
      survey_id: Date.now(),
      token: token,
      employee_name: '测试员工',
      status: 'pending',
      expire_at: '',
      link: '/survey?token=' + token
    }
    s.commit('ADD_SURVEY', survey)
  }
  return success({
    survey_token: survey.token,
    employee_name: survey.employee_name,
    status: survey.status,
    expire_at: survey.expire_at || '',
    link: survey.link
  })
})

Mock.mock('/api/v3/admin/surveys', 'get', () => {
  const s = getStore()
  const surveys = s.state.surveys && s.state.surveys.surveys ? s.state.surveys.surveys : []
  return success(surveys)
})

Mock.mock('/api/v3/admin/survey-responses', 'get', () => {
  const s = getStore()
  const responses = s.state.surveys && s.state.surveys.responses ? s.state.surveys.responses : []
  return success(responses)
})

// ==================== 离职反馈 ====================

Mock.mock('/api/v3/admin/departure-feedbacks', 'get', () => {
  const list = getStoreDepartureFeedbacks()
  return success(list)
})

Mock.mock('/api/v3/admin/departure-feedbacks', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const feedbackId = Date.now()
  const empList = getStoreEmployees()
  const emp = empList.find(e => e.employee_id === body.employee_id)
  const feedback = {
    feedback_id: feedbackId,
    employee_id: body.employee_id,
    employee_name: emp ? emp.real_name : (body.employee_name || ''),
    employee_no: emp ? emp.employee_no : '',
    resignation_date: body.resignation_date,
    observed_behaviors: body.observed_behaviors || [],
    behavior_details: body.behavior_details || '',
    suggestions: body.suggestions || '',
    manager_email: s.state.user && s.state.user.email,
    source: 'manual',
    task_id: emp ? emp.task_id : null,
    created_at: new Date().toISOString()
  }
  const list = s.state.departureFeedbacks && s.state.departureFeedbacks.feedbacks
    ? [...s.state.departureFeedbacks.feedbacks]
    : []
  list.unshift(feedback)
  s.commit('SET_FEEDBACKS', list)
  return success({ feedback_id: feedbackId })
})

// ==================== 评估说明 ====================

Mock.mock(/\/api\/v3\/admin\/instructions(\?.*)?$/, 'get', () => {
  return success(getStoreInstructions())
})

Mock.mock('/api/v3/admin/instructions', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const id = Date.now()
  const item = { instruction_id: id, ...body }
  const list = s.state.evaluationInstructions && s.state.evaluationInstructions.instructions
    ? [...s.state.evaluationInstructions.instructions]
    : getStoreInstructions()
  list.unshift(item)
  s.commit('SET_INSTRUCTIONS', list)
  return success({ instruction_id: id })
})

Mock.mock(/\/api\/v3\/admin\/instructions\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const list = s.state.evaluationInstructions && s.state.evaluationInstructions.instructions
    ? [...s.state.evaluationInstructions.instructions]
    : getStoreInstructions()
  const idx = list.findIndex(i => i.instruction_id === body.instruction_id)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...body }
    s.commit('SET_INSTRUCTIONS', list)
  }
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/instructions\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/instructions\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.evaluationInstructions && s.state.evaluationInstructions.instructions
    ? [...s.state.evaluationInstructions.instructions]
    : getStoreInstructions()
  s.commit('SET_INSTRUCTIONS', list.filter(i => i.instruction_id !== id))
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/instructions\/\d+\/toggle$/, 'post', (options) => {
  const id = parseInt(options.url.match(/\/instructions\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.evaluationInstructions && s.state.evaluationInstructions.instructions
    ? [...s.state.evaluationInstructions.instructions]
    : getStoreInstructions()
  const idx = list.findIndex(i => i.instruction_id === id)
  if (idx >= 0) {
    list[idx].status = list[idx].status === 1 ? 0 : 1
    s.commit('SET_INSTRUCTIONS', list)
    return success({ success: true, status: list[idx].status })
  }
  return fail(404, '说明不存在')
})

Mock.mock(/\/api\/v3\/admin\/instructions\/\d+\/set-active$/, 'post', (options) => {
  const id = parseInt(options.url.match(/\/instructions\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.evaluationInstructions && s.state.evaluationInstructions.instructions
    ? [...s.state.evaluationInstructions.instructions]
    : getStoreInstructions()
  list.forEach(i => { i.is_active = 0 })
  const idx = list.findIndex(i => i.instruction_id === id)
  if (idx >= 0) {
    list[idx].is_active = 1
    s.commit('SET_INSTRUCTIONS', list)
    return success({ success: true })
  }
  return fail(404, '说明不存在')
})

Mock.mock('/api/v3/instructions/active', 'get', () => {
  return success(getActiveInstruction())
})

// ==================== 评分规则 ====================

Mock.mock('/api/v3/admin/scoring-rules', 'get', () => {
  return success(getStoreScoringRules())
})

// ==================== 内容管理 ====================

Mock.mock('/api/v3/admin/contents', 'get', () => {
  return success(getStoreReportContents())
})

Mock.mock('/api/v3/admin/contents', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const id = Date.now()
  const item = { content_id: id, ...body }
  const list = s.state.reportContentConfigs && s.state.reportContentConfigs.contents
    ? [...s.state.reportContentConfigs.contents]
    : getStoreReportContents()
  list.push(item)
  s.commit('SET_CONTENTS', list)
  return success({ content_id: id })
})

Mock.mock(/\/api\/v3\/admin\/contents\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const list = s.state.reportContentConfigs && s.state.reportContentConfigs.contents
    ? [...s.state.reportContentConfigs.contents]
    : getStoreReportContents()
  const idx = list.findIndex(c => c.content_id === body.content_id)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...body }
    s.commit('SET_CONTENTS', list)
  }
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/contents\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/contents\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.reportContentConfigs && s.state.reportContentConfigs.contents
    ? [...s.state.reportContentConfigs.contents]
    : getStoreReportContents()
  s.commit('SET_CONTENTS', list.filter(c => c.content_id !== id))
  return success({ success: true })
})

// ==================== 管理员 ====================

Mock.mock('/api/v3/admin/admins', 'get', () => {
  const s = getStore()
  const admins = s.state.admins && s.state.admins.admins && s.state.admins.admins.length
    ? s.state.admins.admins
    : defaultAdmins
  return success(admins)
})

Mock.mock('/api/v3/admin/admins', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const id = Date.now()
  const admin = { admin_id: id, ...body, created_at: new Date().toISOString() }
  const list = s.state.admins && s.state.admins.admins ? [...s.state.admins.admins] : defaultAdmins
  list.push(admin)
  s.commit('SET_ADMINS', list)
  return success({ admin_id: id })
})

Mock.mock(/\/api\/v3\/admin\/admins\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const list = s.state.admins && s.state.admins.admins ? [...s.state.admins.admins] : defaultAdmins
  const idx = list.findIndex(a => a.admin_id === body.admin_id)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...body }
    s.commit('SET_ADMINS', list)
  }
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/admins\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/admins\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.admins && s.state.admins.admins ? [...s.state.admins.admins] : defaultAdmins
  s.commit('SET_ADMINS', list.filter(a => a.admin_id !== id))
  return success({ success: true })
})

// ==================== 系统配置 ====================

Mock.mock('/api/v3/admin/settings', 'get', () => {
  const s = getStore()
  return success(s.state.settings && s.state.settings.settings ? s.state.settings.settings : { thresholds: { lowMax: 4, mediumMax: 10 } })
})

Mock.mock('/api/v3/admin/settings', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const settings = s.state.settings && s.state.settings.settings ? { ...s.state.settings.settings } : {}
  settings[body.config_key] = body.config_value
  s.commit('SET_SETTINGS', settings)
  // 评分规则说明需同步写入 scoringRules store，保证 fetchScoringRules 能读到最新值
  if (body.config_key === 'scoring_rules' && body.config_value) {
    s.commit('SET_SCORING_RULES', body.config_value)
  }
  return success({ success: true })
})

// ==================== 话术模板 ====================

const mockTemplates = [
  { template_id: 1, task_id: null, speech: '默认话术模板内容', faq: '常见问题解答', created_at: '2026-05-01T10:00:00', updated_at: '2026-05-01T10:00:00' }
]

Mock.mock('/api/v3/admin/templates', 'get', () => {
  return success(mockTemplates)
})

Mock.mock('/api/v3/admin/templates', 'post', (options) => {
  const body = JSON.parse(options.body)
  const id = Date.now()
  const item = { template_id: id, ...body, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
  mockTemplates.push(item)
  return success({ template_id: id })
})

Mock.mock(/\/api\/v3\/admin\/templates\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const idx = mockTemplates.findIndex(t => t.template_id === body.template_id)
  if (idx >= 0) {
    mockTemplates[idx] = { ...mockTemplates[idx], ...body, updated_at: new Date().toISOString() }
  }
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/templates\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/templates\/(\d+)/)[1])
  const idx = mockTemplates.findIndex(t => t.template_id === id)
  if (idx >= 0) mockTemplates.splice(idx, 1)
  return success({ success: true })
})

// ==================== 调研题目配置 ====================

Mock.mock('/api/v3/admin/survey-questions', 'get', () => {
  return success(getStoreSurveyQuestions())
})

Mock.mock('/api/v3/admin/survey-questions', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const q = {
    question_id: Date.now(),
    ...body,
    status: 1,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
  s.commit('ADD_SURVEY_QUESTION', q)
  return success(q)
})

Mock.mock(/\/api\/v3\/admin\/survey-questions\/\d+$/, 'put', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const list = s.state.surveys && s.state.surveys.questions ? [...s.state.surveys.questions] : getStoreSurveyQuestions()
  const idx = list.findIndex(q => q.question_id === body.question_id)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...body, updated_at: new Date().toISOString() }
    s.commit('SET_SURVEY_QUESTIONS', list)
  }
  return success({ success: true })
})

Mock.mock(/\/api\/v3\/admin\/survey-questions\/\d+$/, 'delete', (options) => {
  const id = parseInt(options.url.match(/\/survey-questions\/(\d+)/)[1])
  const s = getStore()
  const list = s.state.surveys && s.state.surveys.questions ? [...s.state.surveys.questions] : getStoreSurveyQuestions()
  s.commit('SET_SURVEY_QUESTIONS', list.filter(q => q.question_id !== id))
  return success({ success: true })
})

Mock.mock('/api/v3/admin/survey-questions/export', 'get', () => {
  const list = getStoreSurveyQuestions()
  let csv = '题目内容,题型,是否必填,排序,最多选几项,选项1,选项2,选项3,选项4,选项5\n'
  list.forEach(q => {
    const opts = (q.options || []).concat(['', '', '', '', '']).slice(0, 5)
    csv += [
      q.question_text,
      q.question_type,
      q.required ? '是' : '否',
      q.sort_order,
      q.max_select || '',
      opts[0], opts[1], opts[2], opts[3], opts[4]
    ].join(',') + '\n'
  })
  return success({ csv_content: csv, filename: '调研题目配置.csv' })
})

Mock.mock('/api/v3/admin/survey-questions/import', 'post', (options) => {
  const body = JSON.parse(options.body)
  const s = getStore()
  const lines = (body.csv_content || '').split('\n').filter(l => l.trim())
  if (lines.length < 2) {
    return fail(400, 'CSV 格式不正确')
  }
  const imported = []
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',')
    if (cols.length < 4) continue
    const opts = cols.slice(5).filter(o => o.trim())
    imported.push({
      question_id: Date.now() + i,
      task_id: body.task_id || null,
      question_text: cols[0].trim(),
      question_type: cols[1].trim(),
      required: cols[2].trim() === '是' ? 1 : 0,
      sort_order: parseInt(cols[3]) || 0,
      max_select: cols[4] ? parseInt(cols[4]) || null : null,
      options: opts,
      status: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
  }
  const list = s.state.surveys && s.state.surveys.questions ? [...s.state.surveys.questions] : []
  const merged = [...list, ...imported]
  s.commit('SET_SURVEY_QUESTIONS', merged)
  return success({ imported_count: imported.length })
})

Mock.mock(/\/api\/v3\/surveys\/[^/]+\/questions$/, 'get', (options) => {
  const token = options.url.match(/\/surveys\/([^/]+)\/questions/)[1]
  const s = getStore()
  const surveys = s.state.surveys && s.state.surveys.surveys ? s.state.surveys.surveys : []
  let survey = surveys.find(s => s.token === token)
  // 未找到时创建默认问卷（用于测试）
  if (!survey) {
    survey = {
      survey_id: Date.now(),
      token: token,
      employee_name: '测试员工',
      status: 'pending',
      expire_at: '',
      link: '/survey?token=' + token
    }
    s.commit('ADD_SURVEY', survey)
  }
  const questions = getStoreSurveyQuestions()
  return success({
    survey_token: survey.token,
    employee_name: survey.employee_name,
    expire_at: survey.expire_at || '',
    questions
  })
})
